import React, { useEffect } from 'react';
import { useMutation } from '@apollo/client';

import { trackUnsubscribeSuccessPageLoad } from '../tracking/tealium';
import { useRoutes } from '../routes';

import Subscription from '../common/subscription';
import UnsubscribeError from './unsubscribe-error';
import unsubscribeIcon from './img/unsubscribe-icon.png';

import translations from './.vocab';
import { useLanguage, useTranslations } from '@vocab/react';
import PageLoader from '../page-loader';
import { useTypedSelector } from 'src/reducers';
import type {
  UnsubscribeApacSavedSearchByCodeMutation,
  UnsubscribeApacSavedSearchByCodeMutationVariables,
} from 'src/types/graphql';
import { unsubscribeApacSavedSearchByCode } from 'src/modules/graphql/notifications/saved-search.graphql';
import { browserLogError } from 'src/modules/error-logger/browserLog';
import { compatibility } from '@seek/ca-myprofile-utils';

const UnsubscribeJobAlert = (props: {
  token: string;
  authenticationStatus: string;
}) => {
  const { t } = useTranslations(translations);

  const { language: vocabLanguage } = useLanguage();
  const language =
    compatibility.getCompatibleLanguageFromLocaleString(vocabLanguage);

  const { homePath, savedSearchesPath } = useRoutes();

  const { token, authenticationStatus } = props;

  const zone = useTypedSelector((store) => store.page.pageAnalytics.zone);
  const [unsubscribeJobAlert, { data, error }] = useMutation<
    UnsubscribeApacSavedSearchByCodeMutation,
    UnsubscribeApacSavedSearchByCodeMutationVariables
  >(unsubscribeApacSavedSearchByCode);

  const authenticated = authenticationStatus === 'authenticated';
  const savedSearchPayload = data?.unsubscribeApacSavedSearchByCode;
  const isFailurePayload = savedSearchPayload && 'error' in savedSearchPayload;

  useEffect(() => {
    if (token) {
      unsubscribeJobAlert({ variables: { input: { code: token } } }).catch(
        (e) => {
          browserLogError(e);
        },
      );
    }
  }, [unsubscribeJobAlert, token]);

  if (error || isFailurePayload) {
    return <UnsubscribeError language={language} zone={zone} />;
  }

  if (savedSearchPayload && !isFailurePayload) {
    trackUnsubscribeSuccessPageLoad('jobAlert', 'email');

    return (
      <Subscription
        title={t('ja-subscription-title')}
        cardTitle={t('ja-subscription-card-title')}
        body={t('ja-subscription-body')}
        icon={unsubscribeIcon}
        link={authenticated ? savedSearchesPath : homePath}
        linkMessage={
          authenticated
            ? t('ja-subscription-link-message-authenticated')
            : t('ja-subscription-link-message-unauthenticated')
        }
      />
    );
  }

  return <PageLoader />;
};

export default UnsubscribeJobAlert;
